// src/components/ui/ShareButton.tsx
/**
 * Reusable Share Button Component
 *
 * Allows sharing game achievements, discoveries, and scores to social media
 */

import React from 'react';
import { Share2 } from 'lucide-react';
import { shareContent, triggerHaptic } from '../../services/capacitorService';
import { colors } from '../../theme/designTokens';

interface ShareButtonProps {
  title: string;
  text: string;
  url?: string;
  className?: string;
  size?: 'small' | 'medium' | 'large';
  variant?: 'primary' | 'secondary' | 'outline';
}

const ShareButton: React.FC<ShareButtonProps> = ({
  title,
  text,
  url,
  className = '',
  size = 'medium',
  variant = 'primary',
}) => {
  const handleShare = async () => {
    triggerHaptic('medium');
    await shareContent({
      title,
      text,
      url: url || window.location.href,
      dialogTitle: 'Share your achievement',
    });
  };

  const sizeStyles = {
    small: {
      padding: '0.5rem 1rem',
      fontSize: '0.875rem',
      iconSize: 16,
    },
    medium: {
      padding: '0.75rem 1.5rem',
      fontSize: '1rem',
      iconSize: 20,
    },
    large: {
      padding: '1rem 2rem',
      fontSize: '1.125rem',
      iconSize: 24,
    },
  };

  const variantStyles = {
    primary: {
      background: colors.gradients.primary,
      color: 'white',
      border: 'none',
      boxShadow: `0 4px 16px ${colors.brand.primary}40`,
    },
    secondary: {
      background: colors.gradients.secondary,
      color: 'white',
      border: 'none',
      boxShadow: `0 4px 16px ${colors.brand.accent}40`,
    },
    outline: {
      background: 'transparent',
      color: colors.brand.primary,
      border: `2px solid ${colors.brand.primary}`,
      boxShadow: 'none',
    },
  };

  const style = {
    ...sizeStyles[size],
    ...variantStyles[variant],
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.5rem',
    borderRadius: '12px',
    cursor: 'pointer',
    fontWeight: '600',
    transition: 'all 0.3s ease',
  };

  return (
    <button
      onClick={handleShare}
      className={className}
      style={style}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-2px)';
        if (variant !== 'outline') {
          e.currentTarget.style.boxShadow = `0 6px 20px ${colors.brand.primary}60`;
        }
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = variantStyles[variant].boxShadow;
      }}
    >
      <Share2 size={sizeStyles[size].iconSize} />
      <span>Share</span>
    </button>
  );
};

export default ShareButton;
